import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarMonth as CalendarIcon,
  Business as MachineIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import {
  fetchProductionMachines,
  fetchProductionJobs,
  completeProductionJob,
  uncompleteProductionJob
} from '../services/productionPlanService';
import { DEMO_MODE, demoMachines } from '../services/demoData';

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString("no-NO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  } catch {
    return String(dt);
  }
}

function formatDate(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleDateString("no-NO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  } catch {
    return String(dt);
  }
}

const MobileProductionPlan = () => {
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filtre
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMachineId, setSelectedMachineId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'completed'
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'today', 'week', 'past', 'future'
  
  // Dialog state
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const getToken = () => localStorage.getItem('token');

  // Last data
  const loadData = async () => {
    setLoading(true);
    setError('');
    
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      console.log('[MobileProductionPlan] Demo mode - using demo data');
      setMachines(demoMachines);
      setJobs([
        { id: 1, order_number: 'AO-2024-001', customer: 'DEMO Kunde AS', machine_id: 1, machine_name: 'Vannskjærer 1', start_time: new Date().toISOString(), end_time: new Date(Date.now() + 3600000).toISOString(), completed: false },
        { id: 2, order_number: 'AO-2024-002', customer: 'Test Bedrift AS', machine_id: 2, machine_name: 'Plasma 1', start_time: new Date(Date.now() + 7200000).toISOString(), end_time: new Date(Date.now() + 10800000).toISOString(), completed: false },
      ]);
      setLoading(false);
      return;
    }
    
    try {
      const token = getToken();
      const [machinesData, jobsData] = await Promise.all([
        fetchProductionMachines(token),
        fetchProductionJobs(token)
      ]);
      
      setMachines(Array.isArray(machinesData) ? machinesData : []);
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (err) {
      console.error('Error loading production plan:', err);
      setError('Kunne ikke laste produksjonsplan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrer jobber
  useEffect(() => {
    let filtered = [...jobs];

    // Søk
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        (job.order_number || '').toLowerCase().includes(search) ||
        (job.customer || '').toLowerCase().includes(search) ||
        (job.memo || '').toLowerCase().includes(search)
      );
    }

    // Maskin-filter
    if (selectedMachineId !== 'all') {
      filtered = filtered.filter(job => String(job.machine_id) === selectedMachineId);
    }

    // Status-filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(job => !job.completed);
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(job => job.completed);
    }

    // Tid-filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    if (timeFilter === 'today') {
      filtered = filtered.filter(job => {
        const start = new Date(job.start_time);
        return start >= today && start < new Date(today.getTime() + 24 * 60 * 60 * 1000);
      });
    } else if (timeFilter === 'week') {
      filtered = filtered.filter(job => {
        const start = new Date(job.start_time);
        return start >= now && start <= weekFromNow;
      });
    } else if (timeFilter === 'past') {
      filtered = filtered.filter(job => {
        const end = new Date(job.end_time);
        return end < now;
      });
    } else if (timeFilter === 'future') {
      filtered = filtered.filter(job => {
        const start = new Date(job.start_time);
        return start > now;
      });
    }

    // Sorter etter starttid
    filtered.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    setFilteredJobs(filtered);
  }, [jobs, searchTerm, selectedMachineId, statusFilter, timeFilter]);

  // Håndter jobb-klikk
  const handleJobClick = (job) => {
    setSelectedJob(job);
    setDetailsOpen(true);
  };

  // Håndter fullføring
  const handleToggleComplete = async () => {
    if (!selectedJob) return;

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      const token = getToken();
      if (selectedJob.completed) {
        await uncompleteProductionJob(selectedJob.id, token);
        setSuccess('Jobb markert som ikke ferdig');
      } else {
        // Send today's date to mark only today as completed
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        await completeProductionJob(selectedJob.id, token, today);
        setSuccess('Dagens planlegging markert som ferdig');
      }
      
      setDetailsOpen(false);
      setSelectedJob(null);
      await loadData();
    } catch (err) {
      console.error('Error toggling job completion:', err);
      setError('Kunne ikke endre status');
    } finally {
      setUpdating(false);
    }
  };

  const getMachineName = (machineId) => {
    const machine = machines.find(m => m.id === machineId);
    return machine ? machine.name : `Maskin ${machineId}`;
  };

  // Sjekk om jobb er i fortid
  const isJobInPast = (job) => {
    return new Date(job.end_time) < new Date();
  };

  // Sjekk om jobb er aktiv nå
  const isJobActive = (job) => {
    const now = new Date();
    return new Date(job.start_time) <= now && new Date(job.end_time) >= now;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, px: 1 }}>
        Produksjonsplan
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Søk */}
      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <TextField
            fullWidth
            placeholder="Søk ordre, kunde eller notat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </CardContent>
      </Card>

      {/* Filtre */}
      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight="bold">
              Filtre
            </Typography>
          </Box>

          {/* Maskin-filter */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Maskin</InputLabel>
            <Select
              value={selectedMachineId}
              onChange={(e) => setSelectedMachineId(e.target.value)}
              label="Maskin"
            >
              <MenuItem value="all">Alle maskiner</MenuItem>
              {machines.map(machine => (
                <MenuItem key={machine.id} value={String(machine.id)}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status-filter */}
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(e, newValue) => newValue && setStatusFilter(newValue)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="active">Aktive</ToggleButton>
            <ToggleButton value="all">Alle</ToggleButton>
            <ToggleButton value="completed">Ferdig</ToggleButton>
          </ToggleButtonGroup>

          {/* Tid-filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Tidsperiode</InputLabel>
            <Select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              label="Tidsperiode"
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="today">I dag</MenuItem>
              <MenuItem value="week">Neste 7 dager</MenuItem>
              <MenuItem value="past">Tidligere</MenuItem>
              <MenuItem value="future">Fremtidig</MenuItem>
            </Select>
          </FormControl>

          {/* Antall resultater */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Viser {filteredJobs.length} av {jobs.length} jobber
          </Typography>
        </CardContent>
      </Card>

      {/* Laster */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Ingen resultater */}
      {!loading && filteredJobs.length === 0 && (
        <Alert severity="info">
          Ingen jobber funnet med disse filtrene
        </Alert>
      )}

      {/* Jobber liste */}
      {!loading && filteredJobs.map((job) => {
        const isPast = isJobInPast(job);
        const isActive = isJobActive(job);
        
        return (
          <Card
            key={job.id}
            elevation={2}
            onClick={() => handleJobClick(job)}
            sx={{
              mb: 2,
              cursor: 'pointer',
              borderLeft: job.completed ? '4px solid #16a34a' : 
                          isActive ? '4px solid #f59e0b' : 
                          '4px solid #3b82f6',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {job.order_number || 'Ingen ordrenr'}
                  </Typography>
                  {job.customer && (
                    <Typography variant="body2" color="text.secondary">
                      {job.customer}
                    </Typography>
                  )}
                </Box>
                {job.completed ? (
                  <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} />
                ) : isActive ? (
                  <Chip label="PÅGÅR" size="small" color="warning" />
                ) : isPast ? (
                  <Chip label="UTGÅTT" size="small" color="default" />
                ) : (
                  <UncheckedIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                )}
              </Box>

              {/* Maskin */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MachineIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {getMachineName(job.machine_id)}
                </Typography>
              </Box>

              {/* Tid */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TimeIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(job.start_time)} {new Date(job.start_time).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                  {' → '}
                  {new Date(job.end_time).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>

              {/* Memo */}
              {job.memo && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    📝 {job.memo}
                  </Typography>
                </Box>
              )}

              {/* Opprettet av */}
              {job.created_by_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {job.created_by_email}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Detaljer Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        {selectedJob && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="primary" />
                  <Typography variant="h6">Jobbdetaljer</Typography>
                </Box>
                <IconButton onClick={() => setDetailsOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              {/* Ordrenummer */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Ordrenummer
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedJob.order_number || '—'}
                </Typography>
              </Box>

              {/* Kunde */}
              {selectedJob.customer && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Kunde
                  </Typography>
                  <Typography variant="body1">
                    {selectedJob.customer}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Maskin */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Maskin
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {getMachineName(selectedJob.machine_id)}
                </Typography>
              </Box>

              {/* Tid */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Start
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedJob.start_time)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Slutt
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedJob.end_time)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Memo */}
              {selectedJob.memo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Notat
                  </Typography>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mt: 0.5 }}>
                    <Typography variant="body2">
                      {selectedJob.memo}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Opprettet av */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Opprettet av
                </Typography>
                <Typography variant="body2">
                  {selectedJob.created_by_email || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(selectedJob.created_at)}
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ p: 2, bgcolor: selectedJob.completed ? '#dcfce7' : '#fef3c7', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedJob.completed ? (
                    <>
                      <CheckCircleIcon sx={{ color: '#16a34a' }} />
                      <Typography variant="body1" fontWeight="bold" color="#15803d">
                        Ferdig
                      </Typography>
                    </>
                  ) : (
                    <>
                      <UncheckedIcon sx={{ color: '#f59e0b' }} />
                      <Typography variant="body1" fontWeight="bold" color="#b45309">
                        Ikke ferdig
                      </Typography>
                    </>
                  )}
                </Box>
                {selectedJob.completed && selectedJob.completed_by_email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Fullført av: {selectedJob.completed_by_email}
                    <br />
                    {formatDateTime(selectedJob.completed_at)}
                  </Typography>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)} color="inherit">
                Lukk
              </Button>
              <Button
                onClick={handleToggleComplete}
                variant="contained"
                color={selectedJob.completed ? "inherit" : "success"}
                disabled={updating}
                startIcon={updating ? <CircularProgress size={20} /> : null}
              >
                {updating ? 'Oppdaterer...' : selectedJob.completed ? 'Marker som ikke ferdig' : 'Marker som ferdig'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MobileProductionPlan;
